
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { BranchPasswordPolicySettings, RegisterRequirements, SecurityQuestion } from '@arland-bmnext/api-data';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import React, { useState } from 'react';
import { BackButton, ContainedButton } from '../../components/core/Button';
import View from '../../components/layout/View';
import { useAccountClient, useBanksClient, useContentClient } from '../../lib/api-client';
import { buildSessionData, withPageSession } from '../../lib/session';
import RegistrationComplete from '../../public/registration-complete.svg';
import { setNoCacheHeader } from '../../util/caching';
import { CmsPageIdentifier } from '../../lib/content';
import { PageFilterType } from '../../filter/types';
import Logo from '../../components/layout/Logo';
import { RegistrationForm, VersionableCheckPoint, CheckPoint } from '../../components/account/forms/registration/RegisterForm';
import { getCmsContentDataFieldValue } from '../../util/content';
import { Bank } from '@arland-bmnext/api-data';
import { useExatoFaceRecognition } from '../../hooks/useExatoFaceRecognition';
import { FaceRecognitionModal } from '../../components/account/modals/FaceRecognitionModal';
import { GqlContent, tryGetBoolFieldValue, tryGetHtmlFieldValue } from '../../lib/api-client/content/model';
enum RegisterState {
    RegisterForm,
    RegisterVerifyEmail
}
const checkPoint = (c: GqlContent, enabledField: string, textField: string) => {
    return {
        label: tryGetHtmlFieldValue(c, textField),
        enabled: tryGetBoolFieldValue(c, enabledField)
    } as CheckPoint;
};
const versionableCheckPoint = (checkPoint: CheckPoint, c: GqlContent) => {
    return {
        ...checkPoint,
        title: tryGetHtmlFieldValue(c, 'meta_title'),
        body: tryGetHtmlFieldValue(c, 'body'),
        version: c.version,
    } as VersionableCheckPoint;
};
type RegisterPageProps = {
    cmsPage: any;
    requirements: RegisterRequirements;
    passwordPolicy: BranchPasswordPolicySettings;
    securityQuestions: SecurityQuestion[];
    termsAndConditions: GqlContent | null;
    privacyPolicy: GqlContent | null;
    banks: Bank[];
};
const Register = ({ cmsPage, requirements, passwordPolicy, securityQuestions, termsAndConditions, privacyPolicy, banks, }: RegisterPageProps) => {
    const { t } = useTranslation('register');
    const [registerState, setRegisterState] = useState(RegisterState.RegisterForm);
    const [emailToVerify, setEmailToVerify] = useState('');
    const exatoFaceRecognition = useExatoFaceRecognition();
    const body = getCmsContentDataFieldValue(cmsPage, 'body');
    const headline = getCmsContentDataFieldValue(cmsPage, 'head_line');
    const redirectTo = getCmsContentDataFieldValue(cmsPage, 'redirect_to');
    const showBonusAcceptanceSelector = getCmsContentDataFieldValue(cmsPage, 'show_bonus_acceptance_select');
    const showPromotionCodeField = getCmsContentDataFieldValue(cmsPage, 'show_promotion_code_field');
    const optInMarketing = checkPoint(cmsPage, 'opt_in_marketing_text_enabled', 'opt_in_marketing_text');
    const financeMinistryCheckpoint = checkPoint(cmsPage, 'accept_data_send_to_ministry_of_finance_enabled', 'accept_data_send_to_ministry_of_finance_text');
    const termsAndConditionsCheckPoint = versionableCheckPoint(checkPoint(cmsPage, 'terms_and_conditions_text_enabled', 'terms_and_conditions_text'), termsAndConditions);
    const privacyPolicyCheckPoint = versionableCheckPoint(checkPoint(cmsPage, 'privacy_policy_text_enabled', 'privacy_policy_text'), privacyPolicy);
    const onRegistrationEmailVerificationNeeded = (email: string) => {
        setRegisterState(RegisterState.RegisterVerifyEmail);
        setEmailToVerify(email);
    };
    return (<div className="register px-4 py-8">
      {registerState === RegisterState.RegisterForm && (<>
          <div className="register-header flex items-center mb-8 h-[42px]">
            <div className="flex items-center h-full aspect-square">
              <BackButton url="/sports"/>
            </div>

            <Logo />

            <div className="flex h-full aspect-square"></div>
          </div>

          <div className="register-form-wrapper flex flex-col  md:rounded-xl md:shadow-3xl md:p-8 md:bg-card md:border-[1px] border-neutral">
            <RegistrationForm title={headline} description={body} requirements={requirements} passwordPolicy={passwordPolicy} securityQuestions={securityQuestions} banks={banks} onRegistrationEmailVerificationNeeded={onRegistrationEmailVerificationNeeded} redirectTo={redirectTo} showBonusAcceptanceSelector={showBonusAcceptanceSelector} showPromotionCodeField={showPromotionCodeField} optInMarketing={optInMarketing} financeMinistryCheckPoint={financeMinistryCheckpoint} termsAndConditionsCheckPoint={termsAndConditionsCheckPoint} privacyPolicyCheckPoint={privacyPolicyCheckPoint} exatoFaceRecognitionFormProps={exatoFaceRecognition.formProps}/>

            <span className="register-footer flex justify-center text-xs mt-1 p-2">
              <span className="register-footer-subtitle">{t('RegisterPage.subtitle')}&nbsp;</span>
              <Link href="/account/login" className="register-footer-login text-primary font-semibold">
                {t('RegisterPage.login')}
              </Link>
            </span>
          </div>

          {exatoFaceRecognition.active && (<FaceRecognitionModal onModalClose={exatoFaceRecognition.onFaceRecognitionModalClosed} faceRecognitionUrl={exatoFaceRecognition.url}/>)}
        </>)}

      {registerState === RegisterState.RegisterVerifyEmail && <RegisterCompleteVerifyEmail email={emailToVerify}/>}
    </div>);
};
const RegisterCompleteVerifyEmail = ({ email }) => {
    const { t } = useTranslation();
    return (<div className="registration-complete flex flex-col space-y-8">
      <div className="registration-complete-HEADER flex flex-grow justify-center items-center">
        <Link href="/sports">
          <Logo />
        </Link>
      </div>

      <div className="registration-complete-svg flex justify-center flex-shrink-0 w-full h-[25vh]">
        <RegistrationComplete />
      </div>

      <h1 className="registration-complete-title flex justify-center text-3xl space-x-2 font-semibold uppercase text-center">
        {t('register:RegisterPage.verifyEmailTitle')}
      </h1>

      <div className="registration-complete-description text-center">
        <Trans i18nKey="register:RegisterPage.verifyEmailDescription" values={{ email: email }} components={[<span className="font-semibold"/>]}/>
      </div>

      <Link href="/sports">
        <ContainedButton fullWidth rounded>
          {t('register:RegisterPage.verifyEmailContinue')}
        </ContainedButton>
      </Link>
    </div>);
};
Register.getLayout = function getLayout(_t, _pageProps, page) {
    return (<View pageId="account-register-page" addHeader={false} addFooter={false} addBottomNav={false} maxMainContentWidth={'500px'}>
      {page}
    </View>);
};
const getServerSideProps = withPageSession(async ({ req, res, session, locale }, _pageFilterResult, pageData) => {
    setNoCacheHeader(res);
    if (session.get('authenticated')) {
        return {
            redirect: {
                destination: `/${locale}/sports`,
                permanent: false,
            },
        };
    }
    const accountClient = useAccountClient();
    const contentClient = useContentClient();
    const banksClient = useBanksClient();
    const languageId = session.get('languageId');
    const sessionId = session.get('sessionId');
    const token = session.get('token');
    const sessionData = buildSessionData(req, sessionId, token);
    const branchSecuritySettings = await accountClient.getBranchSecuritySettings(sessionData);
    if (pageData.frontendSettings?.disableRegistrations || branchSecuritySettings.customerRegistrationSuspended) {
        return {
            redirect: {
                destination: `/${locale}/sports`,
                permanent: false,
            },
        };
    }
    const requirements = await accountClient.getRequiredFields(sessionData);
    const passwordPolicy = await accountClient.getPasswordPolicy(sessionData);
    let securityQuestions = (await accountClient.getSecurityQuestions(sessionData)) as any;
    securityQuestions = securityQuestions?.items?.map((x) => {
        const q = x.securityQuestion;
        q.name = q.translations?.find((t) => t.languageId == languageId)?.name ?? q.name;
        return q;
    });
    const termsAndConditions = await contentClient.getContentByIdentifier(sessionData, CmsPageIdentifier.TermsAndConditions, languageId);
    const privacyPolicy = await contentClient.getContentByIdentifier(sessionData, CmsPageIdentifier.Privacy, languageId);
    const banks = await banksClient.getAll(sessionData);
    return {
        props: {
            ...pageData,
            requirements,
            passwordPolicy,
            securityQuestions,
            termsAndConditions,
            privacyPolicy,
            banks,
        } as RegisterPageProps,
    };
}, CmsPageIdentifier.Registration, [PageFilterType.WebsiteRequiresAuthentication, PageFilterType.PageRequiresAuthentication]);
export default Register;

    async function __Next_Translate__getServerSideProps__19633273a3a__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/account/register',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__19633273a3a__ as getServerSideProps }
  